/**
 * PC内嵌页交互 v1.5.3
 * wiki接口查看文档
 * https://www.tapd.cn/65327176/documents/show/1165327176001000399?file_type=word#toc:4
 * 模块(boundAsyncName)：
 * 1. 基础模块 BaseMod
 * 2. 用户相关模块 UserMod
 * 3. 房间模块 RoomMod
 */
let boundJSBridgeAsync = {};
// 判断是否在客户端内 // TODO: 马甲包
export let isInClient = /PCMiz|hydrogenPc|callmemzPc|PCcallmemz/i.test(navigator.userAgent);
let errMsg = {
  code: -1000,
  msg: '请在PC客户端内打开链接',
};

// 字符串转json
let strToJSON = (str) => {
  let json = null;
  if (Object.prototype.toString.call(str) === '[object String]') {
    try {
      json = JSON.parse(str);
    } catch (err) {
      json = str;
    }

    return json;
  }

  return str;
};

let bridgeModObj = {};

/**
 * 绑定客户端对象
 * @params { String } 需要注册的对象名
 * @return { promise } window[对象名]
 */
const fnGetBridgeMod = (modName) => {
  // console.log(modName, bridgeModObj[modName], window[modName]);
  return new Promise((resolve, reject) => {
    if (!isInClient) {
      return reject(errMsg);
    }
    if (typeof CefSharp !== 'undefined') {
      // 如果缓存对象 boundAsync
      if (CefSharp.IsObjectCached(modName) === true) {
        // 从缓存变量中获取
        if (typeof bridgeModObj[modName] !== 'undefined') {
          return resolve(bridgeModObj[modName]);
        }

        // 从window属性中获取
        if (typeof window[modName] !== 'undefined') {
          bridgeModObj[modName] = window[modName];
          return resolve(bridgeModObj[modName]);
        }
      }

      // 重新绑定客户端对象事件
      return CefSharp.BindObjectAsync(modName)
        .then(() => {
          bridgeModObj[modName] = window[modName];
          return resolve(bridgeModObj[modName]);
        })
        .catch((err) => {
          console.error(`${modName} BindObjectAsync error`, err);
          return reject(err);
        });
    } else {
      if (window.chrome) {
        const hostObjects = (window.chrome.webview && window.chrome.webview.hostObjects) || {};
        if (hostObjects && !bridgeModObj[modName]) {
          bridgeModObj = hostObjects;
          return resolve(bridgeModObj[modName]);
        } else if (bridgeModObj[modName]) {
          return resolve();
        } else {
          return reject(`webview不存在hostObjects对象或者${modName}方法`);
        }
      } else {
        return reject(`webview不存在chrome对象`);
      }
    }
  });
};

export let callNative = async (modName, funcName, ...args) => {
  const errMsg = {
    code: -1000,
    msg: '请在PC客户端内打开链接',
  };

  console.log('Call Native', JSON.stringify({ modName, funcName, args: { ...args } }));
  // 如果模块modName未注册则重新注册
  if (!bridgeModObj[modName]) {
    await fnGetBridgeMod(modName);
  }

  if (!funcName) {
    errMsg.msg = `请传入funcName参入`;
    return Promise.reject(errMsg);
  }

  return new Promise((resolve, reject) => {
    if (!isInClient) return reject(errMsg);

    const modObj = bridgeModObj[modName];
    // 模块内存在该方法
    if (modObj && modObj[funcName]) {
      let promise;
      if (args && args.length > 0) {
        promise = modObj[funcName].apply({}, args);
      } else {
        // 传任何东西都会报错
        promise = modObj[funcName]();
      }
      return promise
        .then((data) => resolve(strToJSON(data)))
        .catch((err) => {
          reject(err);
          throw new Error(`Error:${err}`);
        });
    }
    // 模块内不存在该方法
    if (modObj && !modObj[funcName]) {
      console.error(`%cError: ${modName}.${funcName} 方法不存在`, 'color:red');
      return reject(`Error: ${modName}.${funcName} 方法不存在`);
    }
    reject(errMsg);
  });
};

/**
 * @param { String } 如果当前窗口可关闭，则关闭当前窗口
 * @returns { Prmoise }
 */
export let closePopupPage = () => {
  return new Promise((resolve) => {
    window.close();
    resolve();
  });
};

/**
 * 打开新窗口页或浏览器
 * @param { String } 如果当前窗口可关闭，则关闭当前窗口
 * @returns { Prmoise }
 */
export let popupPage = (url, type, width, height) => {
  let params = {};

  params.url = url;
  params.type = type || 0;
  width = Number(width);
  height = Number(height);

  if (isNaN(width) !== NaN) {
    params.width = width;
  }
  if (isNaN(height) !== NaN) {
    params.height = height;
  }

  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'popupPage', params)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 获取客户端版本号
 * @returns { Prmoise }
 */
export let getClientVersion = () => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'getClientVersion')
      .then((data) => {
        resolve({ data });
      })
      .catch((err) => reject(err));
  });
};

/**
 * 打开banner链接
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let openBannerLink = (data) => {
  let tranUrl = data.tranUrl || '';
  let name = data.name || '';
  let bannerUrl = data.bannerUrl || '';

  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'openBannerLink', tranUrl, name, bannerUrl)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 打开普通链接窗口
 * @params { String } 链接
 * @params { Object } 附加数据， { title: 'string', width: 'number', height:'number', canChangeSize: 'boolean|是否可以改变大小' }
 * @returns { Prmoise }
 */
export let createNewWebPage = (url, params) => {
  params = params || {};

  try {
    params = JSON.stringify(params);
  } catch (err) {
    params = '';
  }

  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'createNewWebPage', url, params)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 重置窗口大小
 * @params { String } 链接
 * @params { Object } 附加数据， { title: 'number', width: 'number', height:'number', canChangeSize: 'boolean' }
 * @returns { Prmoise }
 */
export let resetWebPage = (url, params) => {
  params = params || {};

  try {
    params = JSON.stringify(params);
  } catch (err) {
    params = '';
  }

  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'resetWebPage', url, params)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 重启软件到登录界面
 * @returns { Prmoise }
 */
export let restartApp = () => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'restart')
      .then((data) => {
        resolve({ data });
      })
      .catch((err) => reject(err));
  });
};
/**
 * 注销退出
 * @returns { Prmoise }
 */
export let logoutRestart = () => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'offLogRestart')
      .then((data) => {
        resolve({ data });
      })
      .catch((err) => reject(err));
  });
};
/**
 * 通知客户端关闭窗口执行命令，只用于注销业务
 * @params { Number } status, 0: 成功注销
 */
export let cancellationStatus = (status) => {
  status = Number(status);
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'cancellationStatus', status)
      .then((data) => {
        resolve({ data });
      })
      .catch((err) => reject(err));
  });
};

/**
 * 获取设备ID (>= 2.2.4) TODO
 * @returns { Prmoise }
 */
export let getDeviceId = () => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'getDeviceId')
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 打开点单窗口 (>= 2.2.4)
 * @params { Number } tabIndex, 0, 1, 2对应tab序号
 * @returns { Prmoise }
 */
export let openBillShowPeopleWindow = (tabIndex) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'openBillShowPeopleWindow', tabIndex)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 获取点单图片列表
 * @params { Object } {}
 * @returns { Prmoise }
 */
export let getShowBillPic = () => {
  return new Promise((resolve, reject) => {
    callNative('UserMod', 'showBillPic')
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 打开TA的咪窝
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let showUserProfile = (userId) => {
  userId = Number(userId);

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'showUserProfile', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 打开TA的个人资料卡 version > 2.0.2
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let showVisitingCard = (userId) => {
  userId = Number(userId);

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'showVisitingCard', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 关注TA
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let subscribeUser = (userId) => {
  userId = Number(userId);

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'subscribeUser', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 取消关注TA
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let unsubscribeUser = (userId) => {
  userId = Number(userId);

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'unsubscribeUser', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/** 发送消息给TA
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let openIMMessageWindow = (userId, messages) => {
  userId = Number(userId);

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'openIMMessageWindow', userId, messages)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 从客户端获取用户登录状态
 * @returns { Prmoise }
 */
export let getLoginInfo = () => {
  return callNative('UserMod', 'getLoginInfo').then((res) => {
    let data = null;

    try {
      if (res.code === 0) {
        data = JSON.parse(res.data);
        return data;
      } else {
        return Promise.reject(res);
      }
    } catch (err) {
      return Promise.reject(err);
    }
  });
};

/**
 * 从客户端获取临时登录状态
 * @returns { Prmoise }
 */
export let getLoginingToken = () => {
  return new Promise((resolve, reject) => {
    callNative('UserMod', 'getLoginingToken')
      .then((res) => {
        let data = null;

        try {
          if (res.code === 0) {
            data = JSON.parse(res.data);
          }
        } catch (err) {}

        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * 发送登录状态给客户端
 * @params { String } 登录会话key
 * @params { Boolean } 是否新用户
 * @returns { Prmoise }
 */
export let sendLoginInfo = (key, isNew) => {
  isNew = typeof isNew !== 'undefined' ? isNew : false;

  return new Promise((resolve, reject) => {
    callNative('UserMod', 'sendLoginInfo', key, isNew)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 更改手机号成功后回调客户端
 * @params { String } 登录会话key
 * @params { Boolean } 是否新用户
 * @returns { Prmoise }
 */
export let changePhoneCallBack = () => {
  return new Promise((resolve, reject) => {
    callNative('UserMod', 'changePhoneCallBack')
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 进入TA的房间
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let enterUserRoom = (userId) => {
  return new Promise((resolve, reject) => {
    callNative('RoomMod', 'enterUserRoom', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 进入TA所在的房间
 * @params { userId } 用户ID
 * @returns { Prmoise }
 */
export let enterUserCurrRoom = (userId) => {
  return new Promise((resolve, reject) => {
    callNative('RoomMod', 'enterUserCurrRoom', userId)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 设置客户端标题
 * @params { String } 标题
 * @returns { Prmoise }
 */
export let setClientTitle = (title) => {
  return new Promise((resolve, reject) => {
    documents.title = title || '';
    resolve();
  });
};

/**
 * 在默认浏览器中打开第三方链接（version >= 2.2.4）
 * @params { url }, 第三方链接
 * @returns { Prmoise }
 */
export let openIExplorer = (url) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'openIExplorer', url)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 播放爵位动画
 * @params { id }, 爵位id
 * @returns { Prmoise }
 */
export let notifyShowEffect = (id) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'notifyShowEffect', id)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 通知客户端当前窗口关闭前 要调用前端的方法fnWinCloseCallback。注意定义fnWinCloseCallback方法
 * 调用时机： 页面加载完毕后
 * @params { url }, 页面url
 * @returns { Prmoise }
 */
export let webSetClosePreCondition = (url) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'webSetClosePreCondition', url)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 文件上传回调
 * @params { param }, 对象
 * @returns { Prmoise }
 */
export let pcUpLoadLog = (param) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'pcUpLoadLog', param)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        console.log(BaseMod);
        reject(err);
      });
  });
};

/**
 * 日志上传（允许未登录上传）
 * @params { param }, 对象
 * @returns { Prmoise }
 */
export let uploadVideoCallback = (param) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'uploadVideoCallback', param)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        console.log(BaseMod);
        reject(err);
      });
  });
};

/**
 * 窗口大小
 * @params{
 *  size: 0正常 1最小化 2最大化
 * }
 * @returns { Prmoise }
 */
export let resizeMode = (size) => {
  return new Promise((resolve, reject) => {
    callNative('BrowserControlCtrl', 'resizeMode', size)
      .then((data) => {
        resolve({ data });
      })
      .catch((err) => reject(err));
  });
};

/**
 * PC客户端  路由跳转方法
 * @params { url },
 * @returns { Prmoise }
 */
export let toProcessRout = (url) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'toProcessRout', url)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

/**
 * 订阅
 * @params { url },
 * @returns { Prmoise }
 */
export let subscribe = (params) => {
  return new Promise((resolve, reject) => {
    callNative('BaseMod', 'subscribe', params)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => reject(err));
  });
};

export default {
  openBillShowPeopleWindow,
  getDeviceId,
  openIExplorer,
  changePhoneCallBack,
  getShowBillPic,
  resetWebPage,
  cancellationStatus,
  restartApp,
  createNewWebPage,
  openBannerLink,
  isInClient,
  closePopupPage,
  popupPage,
  getClientVersion,
  setClientTitle,
  showUserProfile,
  showVisitingCard,
  subscribeUser,
  unsubscribeUser,
  openIMMessageWindow,
  getLoginingToken,
  getLoginInfo,
  sendLoginInfo,
  enterUserRoom,
  enterUserCurrRoom,
  callNative,
  notifyShowEffect,
  webSetClosePreCondition,
  pcUpLoadLog,
  uploadVideoCallback,
  resizeMode,
  toProcessRout,
  subscribe,
};
